"use client";

import type { PropsWithChildren } from "react";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { isLegalPagePath } from "@/lib/legal-pages";

interface CSSFadeUpInProps extends PropsWithChildren {
  className?: string;
  delay?: number;
  duration?: number;
  viewport?: boolean;
}

export function CSSFadeUpIn({
  children,
  className,
  delay = 0,
  duration = 0.5,
  viewport = true,
}: CSSFadeUpInProps) {
  const pathname = usePathname();
  if (isLegalPagePath(pathname)) {
    return <div className={className}>{children}</div>;
  }

  const variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration,
        delay,
        ease: "easeOut" as const,
      },
    },
  };

  if (viewport) {
    return (
      <motion.div
        variants={variants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div variants={variants} initial="hidden" animate="visible" className={className}>
      {children}
    </motion.div>
  );
}
